/* eslint-disable jsdoc/require-returns */

/* eslint-disable jsdoc/check-param-names */
/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

/**
 * Styled Components
 */
const TableWrapper = superstyled({
  name: 'TableSection',
  slot: 'Wrapper',
  tag: Box,
  styles: ({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.rounded.md,
    backgroundColor: theme.palette.background.paper
  })
});

const StyledTable = superstyled({
  name: 'TableSection',
  slot: 'Table',
  tag: 'table',
  styles: () => ({
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  })
});

const TableHead = superstyled({
  name: 'TableSection',
  slot: 'Head',
  tag: 'thead',
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.border.main}`
  })
});

const TableHeaderCell = superstyled({
  name: 'TableSection',
  slot: 'HeaderCell',
  tag: 'th',
  propsNeeded: ['highlighted'],
  styles: ({ theme, highlighted = false }) => ({
    padding: theme.spacing(2),
    textAlign: 'left',
    fontWeight: '500',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    ...(highlighted && {
      backgroundColor: '#D1FDFF'
    })
  })
});

const TableBody = superstyled({
  name: 'TableSection',
  slot: 'Body',
  tag: 'tbody',
  styles: () => ({})
});

const TableRow = superstyled({
  name: 'TableSection',
  slot: 'Row',
  tag: 'tr',
  styles: ({ theme, striped }) => ({
    borderBottom: `1px solid ${theme.palette.border.light}`,
    ...(striped && {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.background.default
      }
    }),
    '&:last-child': {
      borderBottom: 'none'
    }
  })
});

const TableCell = superstyled({
  name: 'TableSection',
  slot: 'Cell',
  tag: 'td',
  propsNeeded: ['highlighted'],
  styles: ({ theme, highlighted = false }) => ({
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    verticalAlign: 'top',
    ...(highlighted && {
      backgroundColor: '#E1FDFF'
    })
  })
});

const Caption = superstyled({
  name: 'TableSection',
  slot: 'Caption',
  tag: Box,
  styles: ({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.border.main}`,
    backgroundColor: theme.palette.background.default
  })
});

/**
 * TableSection Component
 *
 * Displays data in a formatted table within report sections.
 * Supports responsive design with horizontal scroll on mobile.
 *
 * @param {object} props - Component props
 * @param {object} props.content - Table data
 * @param {string[]} props.content.headers - Array of column header labels
 * @param {Array<string[]>} props.content.rows - Array of row arrays (each row is array of cell values)
 * @param {string} [props.content.caption] - Optional caption text displayed below table
 * @param {boolean} [props.content.striped=true] - Whether to stripe alternate rows
 * @param {number} [props.content.highlightColumn] - Index of column to highlight (0-based)
 *
 * @example
 * <TableSection content={{
 *   headers: ['Segment', 'Market Share', 'Growth Rate'],
 *   rows: [
 *     ['Enterprise', '42%', '+12%'],
 *     ['SMB', '35%', '+8%']
 *   ],
 *   caption: 'Customer Segmentation Breakdown',
 *   striped: true,
 *   highlightColumn: 1
 * }} />
 */
function TableSection({ content }) {
  // const theme = useTheme();

  if (!content || !content.headers || !content.rows) {
    return null;
  }

  const { headers, rows, caption, striped = true, highlightColumn = null } = content;

  return (
    <TableWrapper>
      <StyledTable>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableHeaderCell key={index} highlighted={index === highlightColumn}>
                {header}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex} striped={striped}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex} highlighted={cellIndex === highlightColumn}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
      {caption && (
        <Caption>
          <Text color="secondary" size="sm">
            {caption}
          </Text>
        </Caption>
      )}
    </TableWrapper>
  );
}

TableSection.propTypes = {
  content: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    caption: PropTypes.string,
    striped: PropTypes.bool,
    highlightColumn: PropTypes.number
  }).isRequired
};

export default TableSection;
